import { type NavAction, type NavItem, NavItemTypeEnum, NavItemContext } from '@/common/types/navigation'
import { type IModuleContext } from '@/common/types/module'
import i18n from '@/plugins/i18n'

const { t } = i18n.global

interface IIterableNavItem {
  [Symbol.iterator](): IterableIterator<NavItem>
}

export abstract class BaseNavigationItemBuilder implements IIterableNavItem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: NavAction<any>
  context: IModuleContext

  text: string = ''
  textKey: string = ''

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(context: IModuleContext, action: NavAction<any>) {
    this.action = action
    this.context = context

    this.text = this.getText(action)
  }

  [Symbol.iterator]() {
    return this.getNavigationItem()
  }

  *getNavigationItem() {
    if (typeof this.action.actions === 'undefined' || !this.action.actions || (this.action.actions && this.action.actions.length === 0)) {
      // Single action
      yield* this.buildNavigationItem(this.action, NavItemContext.Default)
    } else if (this.action.actions) {
      if (this.action.actions.length === 1) {
        // Action with a unique child
        yield* this.buildNavigationItem(this.action.actions[0], NavItemContext.Single)
      } else {
        // Action with children. A menu is created to wrap the children items
        const children: NavItem[] = this.action.actions.reduce((accumulator, action) => {
          return [...accumulator, ...this.buildNavigationItem(action, NavItemContext.Default)]
        }, [] as NavItem[])

        yield {
          category: this.action.type,
          text: this.getText(this.action, NavItemContext.Root),
          type: NavItemTypeEnum.List,
          children,
        } as NavItem
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDefaultNavigationItem(action: NavAction<any>, itemContext: NavItemContext, type: NavItemTypeEnum = NavItemTypeEnum.Link): NavItem {
    return {
      category: this.action.type,
      text: this.getText(action, itemContext),
      type,
      payload: {
        ...action.actionData,
      },
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getText(action: NavAction<any>, itemContext: NavItemContext = NavItemContext.Default) {
    // Action with a unique child. An item is build with the
    // child action but the text of the parent action should
    // be displayed. The text of the child action could be a
    // space name that has no meaning regarding the action
    let text: string

    switch (itemContext) {
      case NavItemContext.Single:
      case NavItemContext.Root:
        text = (this.textKey && t(this.textKey)) || (action.key && t(action.key)) || 'Undefined'
        break
      default:
        text = (action.key && t(action.key)) || 'Undefined'
        break
    }

    return text
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract buildNavigationItem(action: NavAction<any>, itemContext: NavItemContext): IterableIterator<NavItem>
}
